import { useCallback, useEffect, useMemo, useState } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import { Badge, Button, Card, Form, ProgressBar, Spinner, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
 Bar,
 BarChart,
 CartesianGrid,
 Cell,
 Pie,
 PieChart,
 ResponsiveContainer,
 Tooltip,
 XAxis,
 YAxis,
} from "recharts";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";

const READINESS_COLORS = {
 Ready: "#28a745",
 "At Risk": "#f0ad4e",
 "Not Ready": "#dc3545",
};

const StatCard = ({ title, value, icon, color, subText }) => (
 <Card className="border-0 shadow-sm h-100">
 <Card.Body className="d-flex align-items-center justify-content-between">
 <div>
 <p className="text-muted small mb-1">{title}</p>
 <h3 className="fw-bold mb-0">{value}</h3>
 {subText ? <span className="text-muted small">{subText}</span> : null}
 </div>
 <div
 className="d-flex align-items-center justify-content-center rounded-circle"
 style={{ width: 48, height: 48, background: `${color}18`, color }}
 >
 <i className={`fas ${icon}`}></i>
 </div>
 </Card.Body>
 </Card>
);

const EmptyState = ({ text }) => (
 <div className="text-center text-muted py-5">
 <i className="fas fa-chart-bar fs-2 mb-2 d-block"></i>
 <span>{text}</span>
 </div>
);

const HotoReadinessDashboard = () => {
 const [dashboard, setDashboard] = useState(null);
 const [workOrders, setWorkOrders] = useState([]);
 const [selectedWorkOrder, setSelectedWorkOrder] = usePersistentState("hoto_wo", "");
 const [status, setStatus] = usePersistentState("hoto_status", "");
 const [search, setSearch] = usePersistentState("hoto_search", "");
 const [loading, setLoading] = useState(true);
 const [refreshing, setRefreshing] = useState(false);

 const loadWorkOrders = useCallback(async () => {
 try {
 const res = await api.get("/work-orders", { suppressErrorToast: true });
 if (res.data?.success) setWorkOrders(res.data.data || []);
 console.log("Work orders loaded:", res.data?.data || []);
 } catch (err) {
 setWorkOrders([]);
 }
 }, []);


 const loadDashboard = useCallback(async () => {
 try {
 const params = {};
 if (selectedWorkOrder) params.workOrder = selectedWorkOrder;
 if (status) params.status = status;
 if (search.trim()) params.search = search.trim();

 const res = await api.get("/handover/readiness-dashboard", { params });
 if (res.data?.success) setDashboard(res.data.data);
 } catch (err) {
 setDashboard(null);
 } finally {
 setLoading(false);
 setRefreshing(false);
 }
 }, [search, selectedWorkOrder, status]);

 useEffect(() => {
 loadWorkOrders();
 }, [loadWorkOrders]);

 useEffect(() => {
 const timer = setTimeout(() => {
 loadDashboard();
 }, 250);
 return () => clearTimeout(timer);
 }, [loadDashboard]);

 const totals = dashboard?.totals || {};
 const readinessBreakdown = (dashboard?.readinessBreakdown || []).filter(Boolean);
 const projectReadiness = (dashboard?.projectReadiness || []).filter(Boolean);
 const blockers = (dashboard?.blockers || []).filter((item) => item && item.value > 0);
 const records = (dashboard?.records || []).filter(Boolean);

 const readinessRate = useMemo(() => {
 if (!totals.total) return 0;
 return Math.round(((totals.ready || 0) / totals.total) * 100);
 }, [totals.ready, totals.total]);

 const refresh = async () => {
 setRefreshing(true);
 await loadDashboard();
 };

 return (
 <>
 <PageTitle activeMenu="HOTO Readiness Dashboard" motherMenu="ITC" />

 <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
 <div>
 <h3 className="fw-bold mb-1">HOTO Readiness Dashboard</h3>
 <p className="text-muted mb-0">Track handover readiness, blockers, approvals, and close-out hygiene.</p>
 </div>
 <Button variant="primary" onClick={refresh} disabled={refreshing}>
 {refreshing ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="fas fa-sync-alt me-2"></i>}
 Refresh
 </Button>
 </div>

 <Card className="border-0 shadow-sm mb-4">
 <Card.Body>
 <div className="row g-3 align-items-end">
 <div className="col-xl-4 col-md-6">
 <Form.Label>Work Order</Form.Label>
 <Form.Select value={selectedWorkOrder} onChange={(e) => setSelectedWorkOrder(e.target.value)}>
 <option value="">All work orders</option>
 {workOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo}{wo.label ? ` – ${wo.label}` : ""}
 </option>
 ))}
 </Form.Select>
 </div>
 <div className="col-xl-3 col-md-6">
 <Form.Label>Approval Status</Form.Label>
 <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
 <option value="">All statuses</option>
 <option value="Pending">Pending</option>
 <option value="Approved">Approved</option>
 <option value="Rejected">Rejected</option>
 </Form.Select>
 </div>
 <div className="col-xl-3 col-md-6">
 <Form.Label>Search</Form.Label>
 <Form.Control
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 placeholder="HO number or BOQ item"
 />
 </div>
 <div className="col-xl-2 col-md-6">
 <Button
 variant="outline-secondary"
 className="w-100"
 onClick={() => {
 setSelectedWorkOrder("");
 setStatus("");
 setSearch("");
 }}
 >
 <i className="fas fa-eraser me-2"></i>
 Clear
 </Button>
 </div>
 </div>
 </Card.Body>
 </Card>

 {loading ? (
 <div className="text-center py-5">
 <Spinner animation="border" />
 <p className="text-muted mt-2">Loading HOTO readiness...</p>
 </div>
 ) : (
 <>
 <div className="row g-3 mb-4">
 <div className="col-xl-3 col-md-6">
 <StatCard title="Total Handovers" value={totals.total || 0} icon="fa-handshake" color="#0078D7" />
 </div>
 <div className="col-xl-3 col-md-6">
 <StatCard title="Ready for HOTO" value={totals.ready || 0} icon="fa-circle-check" color="#28a745" subText={`${readinessRate}% ready rate`} />
 </div>
 <div className="col-xl-3 col-md-6">
 <StatCard title="At Risk" value={totals.atRisk || 0} icon="fa-triangle-exclamation" color="#f0ad4e" />
 </div>
 <div className="col-xl-3 col-md-6">
 <StatCard title="Average Readiness" value={`${totals.avgReadiness || 0}%`} icon="fa-gauge-high" color="#6f42c1" />
 </div>
 </div>

 <div className="row g-3 mb-4">
 <div className="col-xl-4">
 <Card className="border-0 shadow-sm h-100">
 <Card.Header className="bg-transparent border-0 fw-bold">Readiness Mix</Card.Header>
 <Card.Body style={{ minHeight: 280 }}>
 {readinessBreakdown.length ? (
 <ResponsiveContainer width="100%" height={240}>
 <PieChart>
 <Pie data={readinessBreakdown} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={4}>
 {readinessBreakdown.map((entry) => (
 <Cell key={entry.name} fill={READINESS_COLORS[entry.name] || "#0078D7"} />
 ))}
 </Pie>
 <Tooltip />
 </PieChart>
 </ResponsiveContainer>
 ) : (
 <EmptyState text="No readiness data available" />
 )}
 </Card.Body>
 </Card>
 </div>

 <div className="col-xl-4">
 <Card className="border-0 shadow-sm h-100">
 <Card.Header className="bg-transparent border-0 fw-bold">Open Blockers</Card.Header>
 <Card.Body style={{ minHeight: 280 }}>
 {blockers.length ? (
 <ResponsiveContainer width="100%" height={240}>
 <BarChart data={blockers} layout="vertical" margin={{ left: 20, right: 20 }}>
 <CartesianGrid strokeDasharray="3 3" horizontal={false} />
 <XAxis type="number" allowDecimals={false} />
 <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11 }} />
 <Tooltip />
 <Bar dataKey="value" fill="#dc3545" radius={[0, 6, 6, 0]} />
 </BarChart>
 </ResponsiveContainer>
 ) : (
 <EmptyState text="No open blockers" />
 )}
 </Card.Body>
 </Card>
 </div>

 <div className="col-xl-4">
 <Card className="border-0 shadow-sm h-100">
 <Card.Header className="bg-transparent border-0 fw-bold">Project Readiness</Card.Header>
 <Card.Body className="d-flex flex-column gap-3">
 {projectReadiness.length ? projectReadiness.slice(0, 6).map((item, index) => (
 <div key={item?._id || item?.projectName || index}>
 <div className="d-flex justify-content-between small mb-1">
 <span className="fw-semibold text-truncate pe-2">{item?.projectName || "-"}</span>
 <span>{item?.avgReadiness ?? 0}%</span>
 </div>
 <ProgressBar now={item?.avgReadiness ?? 0} variant={(item?.avgReadiness ?? 0) >= 85 ? "success" : (item?.avgReadiness ?? 0) >= 60 ? "warning" : "danger"} />
 </div>
 )) : (
 <EmptyState text="No project readiness data" />
 )}
 </Card.Body>
 </Card>
 </div>
 </div>

 <Card className="border-0 shadow-sm">
 <Card.Header className="bg-transparent border-0 d-flex justify-content-between align-items-center">
 <span className="fw-bold">Handover Readiness Register</span>
 <Badge bg="light" text="dark">{records.length} records</Badge>
 </Card.Header>
 <Card.Body className="p-0">
 <div className="table-responsive">
 <Table hover className="mb-0 align-middle">
 <thead className="table-light">
 <tr>
 <th>Handover</th>
 <th>Project</th>
 <th>Work Order</th>
 <th>Status</th>
 <th>Checklist</th>
 <th>Readiness</th>
 <th>Blockers</th>
 <th className="text-end">Action</th>
 </tr>
 </thead>
 <tbody>
 {records.length ? records.map((item, index) => (
 <tr key={item?._id || item?.hoNumber || index}>
 <td>
 <div className="fw-semibold">{item?.hoNumber || "-"}</div>
 <small className="text-muted">{item?.boqCount || 0} BOQ items, {item?.documentCount || 0} docs</small>
 </td>
 <td>{item?.project?.projectName || "-"}</td>
 <td>{item?.workOrder?.woNo || item?.workOrder?.woNumber || "-"}</td>
 <td><Badge bg={item?.status === "Approved" ? "success" : item?.status === "Rejected" ? "danger" : "warning"}>{item?.status || "Pending"}</Badge></td>
 <td style={{ minWidth: 150 }}>
 <div className="small mb-1">{item?.checklistCompleted || 0}/{item?.checklistTotal || 0}</div>
 <ProgressBar now={item?.checklistPercent || 0} variant={(item?.checklistPercent || 0) === 100 ? "success" : "warning"} />
 </td>
 <td style={{ minWidth: 150 }}>
 <div className="d-flex justify-content-between small mb-1">
 <span>{item?.readinessStatus || "-"}</span>
 <strong>{item?.readinessScore || 0}%</strong>
 </div>
 <ProgressBar now={item?.readinessScore || 0} variant={item?.readinessStatus === "Ready" ? "success" : item?.readinessStatus === "At Risk" ? "warning" : "danger"} />
 </td>
 <td style={{ maxWidth: 260 }}>
 {Array.isArray(item?.blockers) && item.blockers.length ? item.blockers.slice(0, 2).map((blocker) => (
 <Badge bg="light" text="dark" className="me-1 mb-1" key={blocker}>{blocker}</Badge>
 )) : <Badge bg="success">Clear</Badge>}
 </td>
 <td className="text-end">
 {item?._id ? (
 <Link className="btn btn-sm btn-outline-primary" to={`/handover/edit/${item._id}`}>
 <i className="fas fa-arrow-right"></i>
 </Link>
 ) : (
 <button className="btn btn-sm btn-outline-primary" disabled>
 <i className="fas fa-arrow-right"></i>
 </button>
 )}
 </td>
 </tr>
 )) : (
 <tr>
 <td colSpan="8"><EmptyState text="No handovers match the selected filters" /></td>
 </tr>
 )}
 </tbody>
 </Table>
 </div>
 </Card.Body>
 </Card>
 </>
 )}
 </>
 );
};

export default HotoReadinessDashboard;
